import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRouter } from 'next/router';
import { debounce } from 'lodash-es';
import cn from 'classnames';
import { Dialog } from '@headlessui/react';
import { SearchIcon } from 'lucide-react';
import type { SearchProductsResponseBody } from '@/api/catalog-v2/search/products';
import { trackProductsSearched } from '@/lib/analytics/trackProduct';
import i18n from '@/settings/i18n';
import SearchOutput from '../SearchOutput';
import { getSearchQueryFromUrl, sanitizeSearchQuery } from '../../helpers';

const t = i18n.init('shared');

const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

interface Props {
  className: string;
}

const SearchInput = ({ className }: Props) => {
  const router = useRouter();
  const qsQuery = router.query.query;
  const defaultQuery =
    qsQuery != null && !Array.isArray(qsQuery) ? qsQuery : '';

  const [search, setSearch] = useState(defaultQuery);
  const [debouncedSearch, setDebouncedSearch] = useState(defaultQuery);
  const [results, setResults] = useState<SearchProductsResponseBody | null>(
    null,
  );
  const [open, setOpen] = useState(false);

  const anchorRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const preserveSearchRef = useRef(false);

  useEffect(() => {
    setSearch(defaultQuery);
  }, [defaultQuery]);

  useEffect(() => {
    const close = () => setOpen(false);

    // Reset the search bar to the destination page's query on navigation so it
    // never carries a previous page's text. Navigation triggered from the
    // search box itself (selecting a suggestion or submitting) keeps the typed
    // term, since the user searched their way to that page.
    const resetToUrl = (url: string) => {
      if (preserveSearchRef.current) {
        preserveSearchRef.current = false;

        return;
      }

      const nextQuery = getSearchQueryFromUrl(url);

      setSearch(nextQuery);
      setDebouncedSearch(nextQuery);
    };

    const clearPreserve = () => {
      preserveSearchRef.current = false;
    };

    router.events.on('routeChangeStart', close);
    router.events.on('routeChangeComplete', resetToUrl);
    router.events.on('routeChangeError', clearPreserve);

    return () => {
      router.events.off('routeChangeStart', close);
      router.events.off('routeChangeComplete', resetToUrl);
      router.events.off('routeChangeError', clearPreserve);
    };
  }, [router.events]);

  const positionPanel = useCallback(() => {
    const panel = panelRef.current;
    const anchor = anchorRef.current;

    if (panel == null || anchor == null) {
      return;
    }

    const bounds = anchor.getBoundingClientRect();

    panel.style.top = `${bounds.top}px`;
    panel.style.left = `${bounds.left}px`;
    panel.style.width = `${bounds.width}px`;
  }, []);

  const setPanelRef = useCallback(
    (node: HTMLDivElement | null) => {
      panelRef.current = node;

      if (node != null) {
        positionPanel();
      }
    },
    [positionPanel],
  );

  useIsomorphicLayoutEffect(() => {
    if (!open) {
      return undefined;
    }

    positionPanel();
    const raf = requestAnimationFrame(positionPanel);

    window.addEventListener('resize', positionPanel);
    window.addEventListener('scroll', positionPanel, true);

    const observer = new ResizeObserver(positionPanel);

    observer.observe(document.documentElement);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', positionPanel);
      window.removeEventListener('scroll', positionPanel, true);
      observer.disconnect();
    };
  }, [open, positionPanel]);

  const debounceSearch = useMemo(
    () => debounce((query: string) => setDebouncedSearch(query), 300),
    [],
  );

  const close = () => setOpen(false);

  // Selecting a suggestion/trending item navigates away from the search box, so
  // mark this navigation as search-originated to keep the typed term.
  const handleSelect = () => {
    preserveSearchRef.current = true;
    setOpen(false);
  };

  const handleAutofill = (value: string) => {
    setSearch(value);
    setDebouncedSearch(value);
    inputRef.current?.focus();
  };

  const onSubmit = async () => {
    const sanitizedSearch = sanitizeSearchQuery(search);

    setSearch(sanitizedSearch);
    setDebouncedSearch(sanitizedSearch);

    const encodedSearch = encodeURIComponent(sanitizedSearch);

    if (encodedSearch.length === 0) {
      return;
    }

    if (sanitizedSearch !== '' && results != null) {
      trackProductsSearched(sanitizedSearch);
    }

    preserveSearchRef.current = true;
    close();

    await router.push(`/search?query=${encodedSearch}`);
  };

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Search"
        className={cn(
          'flex h-10 items-center gap-2 rounded-full bg-gray-200 px-4 text-left text-[13px] text-gray-700',
          className,
        )}
      >
        <SearchIcon className="h-5 w-5 shrink-0" />
        <span className={cn('truncate', { 'text-gray-700/60': !search })}>
          {search || t`Search by model, brand, color…`}
        </span>
      </button>

      <Dialog
        open={open}
        as="div"
        onClose={close}
        initialFocus={inputRef}
        className="fixed inset-0 z-[70] hidden sm:block"
      >
        <Dialog.Overlay className="fixed inset-0 bg-gray-700/30" />

        <div ref={setPanelRef} className="fixed left-0 top-0 w-full">
          <form
            className="flex h-10 w-full items-center gap-2 rounded-full bg-gray-200 px-4 text-[13px]"
            onSubmit={(e) => {
              e.preventDefault();
              onSubmit();
            }}
          >
            <button
              type="submit"
              className="transform-wiggle flex h-10 shrink-0 items-center justify-center transition-all duration-500 hover:rotate-90 focus:rotate-90 active:rotate-90"
              aria-label="Submit for search"
              id="e2e-searchbar-search-button"
            >
              <SearchIcon className="h-5 w-5" />
            </button>
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => {
                const searchQuery = e.target.value;

                setSearch(searchQuery);
                debounceSearch(searchQuery);
              }}
              placeholder={t`Search by model, brand, color…`}
              className="w-full border-0 bg-gray-200 focus:outline-none"
              aria-label="Search Input"
              id="e2e-searchbar-search-input"
              autoComplete="off"
            />
          </form>
          <SearchOutput
            search={debouncedSearch}
            setResults={setResults}
            onSelect={handleSelect}
            onAutofill={handleAutofill}
          />
        </div>
      </Dialog>
    </>
  );
};

export default SearchInput;
